import { DateRange, EnabledDateRange, Milliseconds } from '@iotv/iotv-v3-types'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../../../util/lambda'
import { RollingPatternConfig, RollingPatternReport } from './types'
import { getReasonableTimestampMsFilter } from '../../../util/time/helpers'

/** 2025 03 NOTE: DELAY ACTUALLY OCCURS BEFORE OPERATION. THIS IS NOT DOCUMENENTED CURRENTLY IN DEVICE FIRMWARE SPEC */

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && true)

const isReasonableTimestamp = getReasonableTimestampMsFilter(10, 10)

export type EvaluateRollingScheduleInput = {
  rollingPatternConfig: RollingPatternReport
}

export type EvaluateRollingScheduleOutput = {
  matchedNow: EnabledDateRange | null
  matchedNext: EnabledDateRange | null
}

export const evaluateRollingSchedule = ({ rollingPatternConfig }: EvaluateRollingScheduleInput): EvaluateRollingScheduleOutput => {
  let res: EvaluateRollingScheduleOutput = { matchedNow: null, matchedNext: null }
  const relativeDate = new Date()
  const { startTimeUtcSeconds, delay, duration, enabled } = rollingPatternConfig
  /** TAG: ROLLING PROGRAMME ENABLED PROVISION */
  const isEnabled = enabled === 1
  const scheduleStartMs = startTimeUtcSeconds * Milliseconds.SECOND
  if (isReasonableTimestamp(scheduleStartMs)) {
    const validSchduleOutput = getCurrentAndNextScheduledInfo({ relativeDate, rollingPatternConfig })
    if (validSchduleOutput) {
      const { cycleTimeMs, durationMs, isInDelayPhase, isInEffect, isInOperationPhase, lastScheduledStartMs, nextScheduledStartMs } = validSchduleOutput
      const isMatchedNow = !!(lastScheduledStartMs && isEnabled) && lastScheduledStartMs + durationMs > relativeDate.valueOf()
      const matchedNow: EnabledDateRange | null = isMatchedNow
        ? { enabled: isEnabled, dateRange: [new Date(lastScheduledStartMs), new Date(lastScheduledStartMs + durationMs)] }
        : null
      const matchedNext: EnabledDateRange | null = isEnabled
        ? { enabled: isEnabled, dateRange: [new Date(nextScheduledStartMs), new Date(nextScheduledStartMs + durationMs)] }
        : null
      res = { matchedNext, matchedNow }
    }
  } else {
    debug && console.log(`WARN: ${scheduleStartMs} is not a reasonable timestamp in ms, not evaluating as validation will throw`)
  }

  return res
}

export type CurrentAndNextScheduleInfoInput = {
  relativeDate: Date
  rollingPatternConfig: RollingPatternConfig
}

export type CurrentAndNextScheduleInfoOutput = {
  /** the beginning of the delayPhase on last cycle */
  lastCycleStartMs: number | null
  /** the beginning of the delayPhase on next cycle */
  nextCycleStartMs: number
  /** the beginning of the operationPhase on last cycle */
  lastScheduledStartMs: number | null
  /** the beginning of the operationPhase on next cycle */
  nextScheduledStartMs: number
  /** schedule in effect because the provided time is greater or equal to the startTimeUtcMins */
  isInEffect: boolean
  /** schedule in effect but has moved to delay */
  isInDelayPhase: boolean
  /** schedule in effect and is in operation phase */
  isInOperationPhase: boolean
  durationMs: number // from rollingPatternConfig
  cycleTimeMs: number // from rollingPatternConfig
} | null

/**
 * @returns most recent and next rolling schedule based on relative date
 * @throws Error if data is not reasonable
 */
export const getCurrentAndNextScheduledInfo = ({ relativeDate, rollingPatternConfig }: CurrentAndNextScheduleInfoInput): CurrentAndNextScheduleInfoOutput => {
  const { startTimeUtcSeconds, delay, duration } = rollingPatternConfig
  const delayMs = delay * Milliseconds.MINUTE
  const durationMs = duration * Milliseconds.MINUTE
  const cycleTimeMs = delayMs + durationMs

  const scheduleStartMs = startTimeUtcSeconds * Milliseconds.SECOND
  if (!isReasonableTimestamp(scheduleStartMs)) throw new Error(`${scheduleStartMs} is not a reasonable timestamp in ms`)
  const relativeTimeMs = relativeDate.valueOf()
  const isInEffect = relativeTimeMs > scheduleStartMs

  // conditions for not isInEffect
  let lastCycleStartMs = null
  let lastScheduledStartMs = null
  let nextCycleStartMs = scheduleStartMs + durationMs
  let nextScheduledStartMs = scheduleStartMs
  let isInDelayPhase = false
  let isInOperationPhase = false

  if (isInEffect) {
    const timeSinceStart = relativeTimeMs - scheduleStartMs
    const timeSinceLastActive = timeSinceStart & cycleTimeMs
    const pastCycles = Math.floor(timeSinceStart / cycleTimeMs)
    lastScheduledStartMs = scheduleStartMs + pastCycles * cycleTimeMs
    lastCycleStartMs = lastScheduledStartMs + durationMs

    nextCycleStartMs = lastScheduledStartMs + cycleTimeMs
    nextScheduledStartMs = lastCycleStartMs + cycleTimeMs

    // is it currently waiting for a programme
    isInOperationPhase = timeSinceLastActive < durationMs
    isInDelayPhase = !!!isInOperationPhase
  }

  let res: CurrentAndNextScheduleInfoOutput = null

  if (rollingPatternConfig.delay === 0 || rollingPatternConfig.duration === 0) {
  } else {
    res = {
      lastCycleStartMs, // last finish time
      lastScheduledStartMs, // last start time
      nextCycleStartMs, // next `finish`
      nextScheduledStartMs, // next start time
      isInEffect, // above start time
      isInDelayPhase, // in waiting
      isInOperationPhase, // in active
      durationMs, // calculated
      cycleTimeMs, // calculated
    }
  }

  return res
}

type RollingScheduleGeneratorInput = {
  startDate: Date
  endDate: Date
  rollingPatternConfig: RollingPatternConfig
}

export const rollingScheduleGenerator = function* ({ startDate, endDate, rollingPatternConfig }: RollingScheduleGeneratorInput) {
  const validSchduleOutput = getCurrentAndNextScheduledInfo({ relativeDate: startDate, rollingPatternConfig })

  if (validSchduleOutput) {
    const { nextScheduledStartMs, durationMs, cycleTimeMs } = validSchduleOutput
    let currentStartMs: number = nextScheduledStartMs
    const endMs = endDate.valueOf()
    /** TAG - DISABLED ROLLING PROGRAMME PROVISION */
    do {
      const dateRange: DateRange = [new Date(currentStartMs), new Date(currentStartMs + durationMs)]
      yield dateRange
      currentStartMs += cycleTimeMs
    } while (currentStartMs <= endMs)
  } else {
    console.log(`NO SCHEDULES TO GENERATE AS  getCurrentAndNextScheduledInfo gave null return`)
  }
  return 'done'
}
